'use client';
import {
  Alert,
  Flex,
  freddyTheme,
  Radio,
  RadioGroup,
  Typography
} from '@coolblue-development/freddy';
import selectLocationTrigger from '@coolblue-development/ga4-events/triggers/VacancyLocationSelector/selectLocationTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useTranslation } from '@i18n/useTranslation';
import { Locations } from '@sharedTypes';

type Props = {
  value: string;
  onSelectLocation: (value: ((location: string) => string) | string) => void;
  locations: Locations;
  isError: boolean;
};

export const LocationSelectorModalBody = ({
  value,
  onSelectLocation,
  locations,
  isError
}: Props) => {
  const { t } = useTranslation('vacancyDetail.heroBanner.locationSelector');
  const { trackEvent } = useAnalytics();

  return (
    <Flex
      direction='column'
      gap={400}
      backgroundColor='secondary'
      padding={600}
      sx={{ borderRadius: '8px' }}
    >
      <Typography marginBottom={0}>{t('description')}</Typography>
      <RadioGroup
        value={value}
        onChange={onSelectLocation}
        name='controlled'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          '& > div': {
            rowGap: '0 !important'
          },
          label: {
            background: freddyTheme.color.background.base,
            color: `${freddyTheme.color.font.interactiveBase} !important`,
            fontWeight: freddyTheme.typography.fontWeight.openSans.bold,
            padding: freddyTheme.spacing[400],
            border: `${freddyTheme.borderWidth[25]} solid ${freddyTheme.color.border.base}`,
            borderWidth: '1px 1px 0',
            '&:first-of-type': {
              borderRadius: `${freddyTheme.borderRadius[100]} ${freddyTheme.borderRadius[100]} 0 0`
            },
            '&:last-of-type': {
              borderRadius: `0 0 ${freddyTheme.borderRadius[100]} ${freddyTheme.borderRadius[100]}`,
              borderBottomWidth: '1px'
            }
          }
        }}
      >
        {locations.map(location => (
          <Radio
            key={location.displayName}
            label={location.displayName}
            value={location.slug.toLowerCase()}
            onClick={() => {
              trackEvent(
                selectLocationTrigger({
                  selection_type: 'selected',
                  index: locations.indexOf(location) + 1,
                  option_name: location.displayName,
                  total_indices: locations.length
                })
              );
            }}
          />
        ))}
      </RadioGroup>
      {isError && <Alert intent='error' description={t('errorMessage')} />}
    </Flex>
  );
};
